import { useCallback, useRef, useState } from 'react'

import lang from '@/lang'
import { ReceiptDmsUploadAction, uploadReceiptFilesToDmsInBatches } from '@/modules/receipt/helpers/dms-receipt-upload'

// FLAG:useDocuAi — THE receipt DMS→DocuAI upload, shared by BOTH entry points: the expense/
// revenue list dropzone and the receipt form (new expense / new revenue).
//
// Extracted because the form had its own second implementation, and that second one was not
// hardened: it uploaded a single file, had no batching, no per-file progress, no retry, and —
// the reason it was reported — it cleared its spinner from a CALLBACK the host had to supply.
// A host that did not supply it (the revenue form) left the spinner turning forever while the
// file had long since reached DMS and been analysed.
//
// The fix is structural rather than a wired-up callback: the progress state lives HERE and is
// settled by the upload's own resolution. `uploadReceiptFilesToDmsInBatches` always resolves —
// it absorbs a per-batch rejection and counts those files as failed — so `progress` returns to
// idle on every path, success or failure. No host can forget to stop the spinner, because no
// host stops it.
//
// What stays with the caller: which files it accepts, what it refreshes afterwards, and what it
// does with the created file ids. Those genuinely differ between a list and a form.

export interface ReceiptUploadFailure {
  failed: number,
  total: number,
  message: string,
  files: File[],
}

export interface ReceiptUploadOutcome {
  /** ids of the files that were created, in upload order */
  fileIds: string[],
  failed: number,
  total: number,
  /** the message the failure tile shows, returned so a caller can toast it in the same tick
   *  instead of reading `failure` state that React has not committed yet */
  failureMessage: string | null,
}

// FLAG:useDocuAi — pick a human-readable reason from executeUpload's `errors` (per-file upload
// error messages) for the failure tile; returns null when none is a usable string, so the caller
// can fall back to a generic count summary.
export const getReceiptUploadFailureMessage = (errors?: unknown[]): string | null => {
  const firstStringError = (errors ?? []).find(
    (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0,
  )
  return firstStringError ?? null
}

export const useReceiptDmsUpload = ({
  type,
  action,
  receiptId,
}: {
  type: string,
  action?: ReceiptDmsUploadAction,
  /** FLAG:useDocuAi — T25: the open receipt an 'uploadForReceipt' upload belongs to. */
  receiptId?: number | string | null,
}) => {
  const [progress, setProgress] = useState({ todo: 0, done: 0 })
  const [failure, setFailure] = useState<ReceiptUploadFailure | null>(null)
  // The files a retry would re-send. Kept in a ref as well so `retry` never closes over a stale
  // failure object.
  const lastFilesRef = useRef<File[]>([])

  const upload = useCallback(async(files: File[]): Promise<ReceiptUploadOutcome> => {
    lastFilesRef.current = files
    setFailure(null)
    if (!files.length) { return { fileIds: [], failed: 0, total: 0, failureMessage: null } }

    setProgress({ todo: files.length, done: 0 })

    let uploaded: Array<{ id: string, name: string }> = []
    let failed = 0
    let reason: string | null = null
    // F15.3 — identity-true failed entries (uuid + original File) from the upload itself.
    let failedEntries: Array<{ uuid?: string, name: string, file?: File }> = []

    try {
      // Bounded sub-batches rather than one giant batch: each settles on its own, so the tile
      // advances and a wedged file cannot hold the entire drop hostage.
      const result = await uploadReceiptFilesToDmsInBatches({
        files,
        type,
        ...(action ? { action } : {}),
        ...(receiptId ? { receiptId } : {}),
        // Real per-file progress. Without it a large drop displayed "0 von N hochgeladen" for its
        // entire duration — indistinguishable from a hang.
        onProgress: ({ done, total }) => setProgress({ todo: total, done }),
      })
      uploaded = result?.data ?? []
      failed = result?.completedWithErrors ?? 0
      reason = getReceiptUploadFailureMessage(result?.errors)
      failedEntries = result?.failedFiles ?? []
    } catch {
      // uploadReceiptFilesToDmsInBatches absorbs a per-batch rejection, so reaching here means
      // something unexpected threw. Treat every file as failed so the caller always leaves the
      // spinner.
      failed = files.length
    }

    // Settled before the failure tile is raised, and outside any conditional: this is the line
    // that makes an endless spinner impossible.
    setProgress({ todo: 0, done: 0 })

    let failureMessage: string | null = null
    if (failed > 0) {
      // F15.3 — the retry set comes from the upload's OWN failed entries (uuid + File reference):
      // a name join misfires when two same-named files were dropped (Scanner-Output 'scan.pdf')
      // and one succeeded — both fell out of the filter and the fallback re-uploaded EVERYTHING,
      // successful file included. The name join stays only as last-resort fallback for the
      // unexpected-throw path, which has no entries.
      const identityFailedFiles = failedEntries
        .map(entry => entry.file)
        .filter((file): file is File => !!file)
      const succeededNames = uploaded.map(entry => entry.name)
      const nameJoinFailedFiles = files.filter(file => !succeededNames.includes(file.name))
      const failedFiles = identityFailedFiles.length > 0 ? identityFailedFiles : nameJoinFailedFiles
      failureMessage = reason ?? lang.receipt.multiUpload.errors.uploadFailedTile(failed, files.length)
      setFailure({
        failed,
        total: files.length,
        message: failureMessage,
        // Retry only the files that did not succeed (avoids re-uploading — and duplicating — the
        // ones that went through). Falls back to all files if we can't tell them apart.
        files: failedFiles.length > 0 ? failedFiles : files,
      })
    }

    return { fileIds: uploaded.map(entry => entry.id), failed, total: files.length, failureMessage }
  }, [type, action, receiptId])

  const retry = useCallback(() => upload(failure?.files ?? lastFilesRef.current), [upload, failure])
  const dismiss = useCallback(() => setFailure(null), [])

  return { progress, failure, upload, retry, dismiss }
}
